import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { identifyUser, resetUser, trackAccountCreated } from '../lib/analytics';
import { persistToken, clearToken, rehydrateToken, TOKEN_KEY, markSignedOut, clearSignedOut, isSignedOut } from '../lib/tokenStore';
import { GOOGLE_NATIVE_FLAG_KEY } from '../lib/nativeAuth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Safe localStorage access (some mobile browsers throw in private mode).
// Writes go through `tokenStore.persistToken` so the token is also
// mirrored into @capacitor/preferences on native (survives iOS ITP).
const safeLocalStorage = {
  get: (key) => {
    try { return localStorage.getItem(key); } catch { return null; }
  },
  set: (key, value) => {
    try { localStorage.setItem(key, value); } catch { /* ignore */ }
  },
  remove: (key) => {
    try { localStorage.removeItem(key); } catch { /* ignore */ }
  }
};

// Build auth headers — uses Bearer token when available (cookie still primary).
// This makes auth robust on iOS Safari ITP / Android strict cookie modes.
const authHeaders = (extra = {}) => {
  const token = safeLocalStorage.get(TOKEN_KEY);
  return token ? { ...extra, Authorization: `Bearer ${token}` } : extra;
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeGoalId, setActiveGoalId] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      if (!safeLocalStorage.get(TOKEN_KEY) && (await isSignedOut())) {
        setUser(null);
        setActiveGoalId(null);
        return;
      }
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include',
        headers: authHeaders(),
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setActiveGoalId(userData.active_goal_id || null);
        // Identify by internal DB user_id only — no PII sent
        identifyUser(userData.user_id, { is_premium: !!userData.is_premium });
      } else {
        setUser(null);
        setActiveGoalId(null);
        if (response.status === 401) {
          // Clear BOTH mirrors — server has rejected the token, so the
          // native-persistent copy is also invalid.
          safeLocalStorage.remove(TOKEN_KEY);
          try { await clearToken(); } catch { /* ignore */ }
        }
      }
    } catch (error) {
      setUser(null);
      setActiveGoalId(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    // Rehydrate the JWT from native-persistent storage (Capacitor
    // Preferences) back into localStorage before the first /auth/me call.
    // On web this is a no-op. On native this is what keeps the user
    // signed in across cold starts when iOS ITP has cleared cookies
    // and localStorage for `app.futureself.es`.
    (async () => {
      try { await rehydrateToken(); } catch { /* ignore */ }
      checkAuth();
    })();
  }, [checkAuth]);

  const login = async (email, password) => {
    let response;
    try {
      response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
    } catch (networkErr) {
      // Network / CORS / TLS failure — surface a clearer message than "Load failed"
      throw new Error('Network error — please check your connection and try again.');
    }

    if (!response.ok) {
      let detail = 'Login failed';
      try { const e = await response.json(); detail = e.detail || detail; } catch { /* ignore */ }
      throw new Error(detail);
    }

    const data = await response.json();
    // Persist JWT token for resilient auth (cookie may be blocked by ITP/strict cookies).
    // On native, also mirrors into Capacitor Preferences so the token
    // survives iOS ITP sweeps across cold starts.
    if (data.token) {
      safeLocalStorage.set(TOKEN_KEY, data.token);
      persistToken(data.token);
    }
    setUser(data);
    setActiveGoalId(data.active_goal_id || null);
    identifyUser(data.user_id, { is_premium: !!data.is_premium });
    return data;
  };

  const register = async (email, password, name) => {
    let response;
    try {
      response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, name }),
      });
    } catch (networkErr) {
      throw new Error('Network error — please check your connection and try again.');
    }

    if (!response.ok) {
      let detail = 'Registration failed';
      try { const e = await response.json(); detail = e.detail || detail; } catch { /* ignore */ }
      throw new Error(detail);
    }

    const data = await response.json();
    if (data.token) {
      safeLocalStorage.set(TOKEN_KEY, data.token);
      persistToken(data.token);
    }
    setUser(data);
    setActiveGoalId(data.active_goal_id || null);
    identifyUser(data.user_id, { is_premium: !!data.is_premium });
    // Fire the approved event — no PII, only the internal ID via identify
    trackAccountCreated({ is_premium: !!data.is_premium, source: 'email_signup' });
    return data;
  };

  const logout = async () => {
    // Clear the local session first so the UI never waits on the network.
    const oldToken = safeLocalStorage.get(TOKEN_KEY);
    const wasGoogle = safeLocalStorage.get(GOOGLE_NATIVE_FLAG_KEY) === '1';
    safeLocalStorage.remove(TOKEN_KEY);
    safeLocalStorage.remove(GOOGLE_NATIVE_FLAG_KEY);
    setUser(null);
    setActiveGoalId(null);
    // Bounded so a stalled native bridge can never block sign-out.
    try {
      await Promise.race([
        Promise.all([clearToken(), markSignedOut()]),
        new Promise((r) => setTimeout(r, 2000)),
      ]);
    } catch { /* ignore */ }
    try { resetUser(); } catch { /* ignore */ }
    // Background clean-up only targets the OLD session and never writes a token.
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 5000);
    fetch(`${API_URL}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: oldToken ? { Authorization: `Bearer ${oldToken}` } : {},
      signal: ctrl.signal,
    }).catch(() => { /* ignore */ }).finally(() => clearTimeout(timer));
    import('../lib/revenuecat').then((m) => m.resetRevenueCatUser()).catch(() => { /* ignore */ });
    if (wasGoogle) {
      import('../lib/nativeAuth').then((m) => m.nativeSignOut()).catch(() => { /* ignore */ });
    }
  };

  /**
   * Adopt the response returned by /api/auth/apple or /api/auth/google/native.
   * Shape matches email/password login: { user_id, email, name, token, ... }.
   * Kept as a separate method so that pages that trigger the native sign-in
   * plugin can hand the result straight to context without re-fetching.
   */
  const loginWithNativeResult = (data) => {
    if (!data) return;
    if (data.token) {
      safeLocalStorage.set(TOKEN_KEY, data.token);
      persistToken(data.token);
    }
    setUser(data);
    setActiveGoalId(data.active_goal_id || null);
    identifyUser(data.user_id, { is_premium: !!data.is_premium });
    return data;
  };

  const setUserData = (userData) => {
    setUser(userData);
    setActiveGoalId(userData?.active_goal_id || null);
    if (userData) clearSignedOut().catch(() => { /* ignore */ });
  };

  // NEW: Activate a goal and update state
  const activateGoal = async (goalId) => {
    try {
      const response = await fetch(`${API_URL}/api/goals/${goalId}/activate`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setActiveGoalId(goalId);
          setUser(prev => prev ? { ...prev, active_goal_id: goalId } : prev);
          return result.data;
        }
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout, 
      setUserData,
      checkAuth,
      activeGoalId,
      setActiveGoalId,
      activateGoal,
      loginWithNativeResult
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
